import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Linkedin, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Logo from './Logo';
import { Container } from './ui';
import { subscribeNewsletter } from '../lib/marketingApi';
import { FAQ_CATEGORIES } from '../data/faqPage';

export default function Footer() {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const year = new Date().getFullYear();

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error(t('form.errorBody'));
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter({ email, source: 'footer', language: lang });
      setDone(true);
      setEmail('');
      toast.success(t('footer.subscribed'));
    } catch (err) {
      toast.error(t('form.errorBody'));
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    {
      title: t('footer.product'),
      links: [
        { to: '/platform', label: t('nav.platform') },
        { to: '/pricing', label: t('nav.pricing') },
        { to: '/how-it-works', label: t('nav.howItWorks') },
        { to: '/demo', label: t('nav.requestDemo') },
        { to: '/book-a-call', label: 'Book a call' },
        { to: '/partners', label: 'Partner with us' },
      ],
    },
    {
      title: t('footer.solutions'),
      links: [
        { to: '/solutions/multifamily', label: t('nav.multifamily') },
        { to: '/solutions/commercial-industrial', label: t('nav.commercial') },
        { to: '/solutions/energy-brokers', label: t('nav.brokers') },
      ],
    },
    {
      title: t('footer.resources'),
      links: [
        { to: '/case-studies', label: t('nav.caseStudies') },
        { to: '/blog', label: t('nav.blog') },
        { to: '/how-it-works', label: t('nav.howItWorks') },
      ],
    },
    {
      title: t('footer.company'),
      links: [
        { to: '/about', label: t('nav.about') },
        { to: '/contact', label: t('nav.contact') },
        { to: '/login', label: t('nav.signIn') },
      ],
    },
    {
      title: 'Support',
      links: [
        { to: '/support', label: 'Contact support' },
        { to: '/support?type=billing', label: 'Billing & credits' },
      ],
    },
    {
      title: 'FAQ',
      links: [
        { to: '/faq', label: 'All FAQs' },
        ...FAQ_CATEGORIES.map((c) => ({ to: `/faq#${c.id}`, label: c.label })),
      ],
    },
  ];

  return (
    <footer className="border-t border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-[#080809]">
      <Container className="py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-600 dark:text-slate-400">{t('footer.tagline')}</p>
            <form onSubmit={onSubscribe} className="mt-6 max-w-sm">
              <div className="text-sm font-semibold text-slate-900 dark:text-white">{t('footer.subscribe')}</div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('footer.subscribeSub')}</p>
              <div className="mt-3 flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.subscribePlaceholder')}
                  data-testid="footer-newsletter-email"
                  className="h-11 w-full rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white"
                />
                <button
                  type="submit"
                  disabled={loading}
                  aria-label={t('footer.subscribeCta')}
                  data-testid="footer-newsletter-submit"
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                </button>
              </div>
              {done && <p className="mt-2 text-xs font-medium text-brand">{t('footer.subscribed')}</p>}
            </form>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-8 lg:grid-cols-6">
            {cols.map((c) => (
              <div key={c.title}>
                <div className="text-sm font-semibold text-slate-900 dark:text-white">{c.title}</div>
                <ul className="mt-3 space-y-2.5">
                  {c.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link to={l.to} className="text-sm text-slate-600 transition-colors hover:text-brand dark:text-slate-400">{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 border-t border-slate-200 pt-6 dark:border-white/10">
          <p className="text-xs leading-relaxed text-slate-400 dark:text-slate-500">{t('footer.disclaimer')}</p>
          <div className="mt-4 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500 dark:text-slate-400">
              <span>© {year} BillPortal Technologies Inc. {t('footer.rights')}</span>
              <Link to="/legal/privacy" data-testid="footer-link-privacy" className="hover:text-brand">{t('footer.privacy')}</Link>
              <Link to="/legal/terms" data-testid="footer-link-terms" className="hover:text-brand">{t('footer.terms')}</Link>
              <Link to="/legal/cookies" data-testid="footer-link-cookies" className="hover:text-brand">{t('footer.cookies')}</Link>
              <Link to="/legal/sla" data-testid="footer-link-sla" className="hover:text-brand">{t('footer.sla')}</Link>
              <Link to="/legal/dpa" data-testid="footer-link-dpa" className="hover:text-brand">{t('footer.dpa')}</Link>
              <Link to="/legal/security" data-testid="footer-link-security" className="hover:text-brand">{t('footer.security')}</Link>
            </div>
            <div className="flex items-center gap-2">
              <a href="https://www.linkedin.com/company/billportal/" target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="footer-social-linkedin" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-brand hover:text-brand dark:border-white/15 dark:text-slate-400"><Linkedin className="h-4 w-4" /></a>
              <a href="mailto:info@billportal.io" aria-label="Email" data-testid="footer-social-email" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-brand hover:text-brand dark:border-white/15 dark:text-slate-400"><Mail className="h-4 w-4" /></a>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
}
